import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Box, Stack, Typography, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor } from '@tiptap/react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { IChatConversation, IChatMessage } from 'src/@types/chat';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { getColorByName } from 'src/components/custom-avatar/CustomAvatar';
import Iconify from 'src/components/iconify';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { formatMessageTime } from 'src/sections/@dashboard/chat/ChatConversation/shared/utils';
import { pxToRem } from 'src/theme/typography';

const ChatMessageContentStyled = styled(ConversationMessageContent)(() => ({
  '&:not(.expanded)': {
    maxWidth: '50ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    width: 'auto',
    fontSize: pxToRem(12),
  },
}));

const Connector = styled('div')(({ theme }) => ({
  width: 20,
  height: 30,
  borderBottomLeftRadius: theme.spacing(1),
  borderLeft: `solid 1.5px ${theme.palette.divider}`,
  borderBottom: `solid 1.5px ${theme.palette.divider}`,
}));

type Props = {
  message: IChatMessage;
  conversation: IChatConversation;
  editor: Editor;
};

export const ChatMessageThread = ({ message, conversation, editor }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const thread = useMemo(() => {
    if (!message || !conversation) return null;
    return message.threadLastMessage?.channel as unknown as IChatConversation;
  }, [message, conversation]);

  const countMessage = useMemo(() => {
    if (!message || !message.threadMessagesCount) return 'No Messages';
    return `${message.threadMessagesCount} Message${message.threadMessagesCount > 1 ? 's' : ''}`;
  }, [message]);

  const sender = useWorkspaceUser(message?.threadLastMessage?.author as string);

  if (!thread) return null;

  const handleOpenThread = () => {
    navigate(PATH_DASHBOARD.chat.thread(message.channel, thread._id), {
      state: { background: location },
    });
  };

  return (
    <Stack direction="row" gap={1} mb={1} ml={4.5}>
      <Box sx={{ pl: 5 }}>
        <Connector />
      </Box>
      <Stack
        sx={{
          background: theme.palette.action.hover,
          padding: theme.spacing(0.5, 0.75),
          borderRadius: 1,
          gap: 0.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
        >
          <Typography
            variant="body2"
            sx={{ wordBreak: 'break-word', fontWeight: 500 }}
          >
            {thread?.threadNumber
              ? `Thread #${thread?.threadNumber}`
              : 'Thread'}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={handleOpenThread}
          >
            <Typography variant="caption" color="primary" fontWeight="500">
              {countMessage}
            </Typography>
            <Iconify
              icon="tabler:chevron-right"
              color={theme.palette.primary.main}
            />
          </Stack>
        </Stack>
        {!!message.threadLastMessage && (
          <Stack direction="row" alignItems="center" gap={1}>
            <UserProfileShortPopoverTrigger userId={sender._id}>
              <CPAvatar
                picture={sender.picture}
                name={sender.name}
                size="uSmall"
              />
            </UserProfileShortPopoverTrigger>
            <Stack direction="row" alignItems="center">
              <Typography
                noWrap
                variant="caption"
                color={getColorByName(sender.name)}
              >
                {sender.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                :
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.secondary}
              >
                <ChatMessageContentStyled
                  message={message.threadLastMessage}
                  editor={editor}
                />
              </Typography>
              <Typography noWrap variant="caption" color="InactiveCaptionText">
                {formatMessageTime(message.threadLastMessage.createdAt)}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
